import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Reward points balance + autoships (subscription) list.
 * Both are DB-backed for authenticated users so state survives across devices.
 * Guests fall back to in-memory defaults.
 */
export function useRewards(initialPoints = 0) {
  const [rewardPts, setRewardPts] = useState<number>(initialPoints);
  const [autoships, setAutoships] = useState<any[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  // track auth
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => { sub.subscription.unsubscribe(); };
  }, []);

  const refresh = useCallback(async (uid: string) => {
    const [{ data: rp }, { data: as }] = await Promise.all([
      supabase
        .from("reward_points")
        .select("balance, created_at")
        .eq("user_id", uid)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("autoships")
        .select("*")
        .eq("user_id", uid)
        .eq("is_active", true)
        .order("next_ship_date", { ascending: true }),
    ]);
    setRewardPts(Number(rp?.balance ?? 0));
    setAutoships(as ?? []);
  }, []);

  useEffect(() => {
    if (!userId) {
      setRewardPts(initialPoints);
      setAutoships([]);
      return;
    }
    void refresh(userId);

    // realtime: refresh on new points or autoship changes
    const ch = supabase
      .channel(`rewards:${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "reward_points", filter: `user_id=eq.${userId}` }, () => void refresh(userId))
      .on("postgres_changes", { event: "*", schema: "public", table: "autoships", filter: `user_id=eq.${userId}` }, () => void refresh(userId))
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [userId, refresh, initialPoints]);

  return { rewardPts, setRewardPts, autoships, setAutoships, refreshRewards: () => userId && refresh(userId) };
}